import { Box, ButtonBase, Popover, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useState, useEffect, useRef } from 'react';

import { AddressPreview, AddressSearch, ADD_PROPERTY_TITLE_SX, ReportSleeve } from './AddPropertyParts';

import { Dialog } from '@/components/common/Dialog';
import { Icon } from '@/components/common/Icon';
import { Button, CloseButton, Field, Meta, selectedSx, TextInput } from '@/components/ds';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import {
  CLIENT_GROUPS_QUERY_KEY,
  useClientGroups,
  useCreateListing,
} from '@/hooks/useClientGroups';
import { createDocuments } from '@/services/api/documents/api';
import { generatePreSignedUrls } from '@/services/api/files';
import { checkPropertyExists, createProperty } from '@/services/api/properties/api';
import { useTranslationPrefix } from '@/translations/i18n';
import { Deal } from '@/types/clientGroups';
import {
  fetchAddressSuggestions,
  formatStreetAddress,
  normalizeUnit,
  parseGoogleAddressComponents,
} from '@/types/maps';

interface AddPropertyModalProps {
  open: boolean;
  onClose: () => void;
  onPropertyAdded?: (address: string) => void;
  /** When true, skips createProperty API call and just returns the validated address */
  submitOnly?: boolean;
  /** When given (and not on a phone), the form drops down from this element as a panel instead of a dialog */
  anchorEl?: HTMLElement | null;
}

interface ParsedAddress {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
}

export const AddPropertyModal = ({
  open,
  onClose,
  onPropertyAdded,
  submitOnly,
  anchorEl,
}: AddPropertyModalProps) => {
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const { beginProcessingIndicator } = useUploaderContext();
  const { groups } = useClientGroups();
  const createListing = useCreateListing();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const placesLib = useMapsLibrary('places');
  const mapsLib = useMapsLibrary('maps');
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (mapsLib) setGeocoder(new google.maps.Geocoder());
  }, [mapsLib]);

  const [addressQuery, setAddressQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<google.maps.places.AutocompletePrediction[]>(
    [],
  );
  const [selectedAddress, setSelectedAddress] =
    useState<google.maps.places.AutocompletePrediction | null>(null);
  const [parsedAddress, setParsedAddress] = useState<ParsedAddress | null>(null);
  const [unit, setUnit] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reportSource, setReportSource] = useState<'seller' | 'buyer'>('seller');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressExists, setAddressExists] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<{ deal: Deal; groupId: string } | null>(null);
  const [dealDropdownOpen, setDealDropdownOpen] = useState(false);
  const [dealSearch, setDealSearch] = useState('');
  const dealDropdownRef = useRef<HTMLDivElement>(null);

  // Debounced address search using the new Places API
  useEffect(() => {
    if (!addressQuery || addressQuery.length < 3 || selectedAddress) {
      if (!addressQuery || addressQuery.length < 3) setAddressOptions([]);
      return;
    }
    if (!placesLib) return;

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const suggestions = await fetchAddressSuggestions(addressQuery, placesLib);
        setAddressOptions(suggestions);
      } catch {
        setAddressOptions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [addressQuery, selectedAddress, placesLib]);

  // Resolve selected prediction → ParsedAddress via Geocoder
  useEffect(() => {
    if (!selectedAddress) {
      setParsedAddress(null);
      setAddressExists(false);
      return;
    }
    if (!geocoder) return;

    setAddressExists(false);
    setError(null);
    let cancelled = false;

    geocoder.geocode({ placeId: selectedAddress.place_id }, (results, status) => {
      if (cancelled) return;
      if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
        setParsedAddress(null);
        return;
      }
      const result = results[0];
      const components = parseGoogleAddressComponents(result.address_components);
      const parsed: ParsedAddress = {
        address: [components.streetNumber, components.street].filter(Boolean).join(' '),
        city: components.city ?? '',
        state: components.state ?? '',
        zipCode: components.zip ?? '',
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng(),
      };
      setUnit(components.unit ?? '');
      setParsedAddress(parsed);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedAddress, geocoder]);

  useEffect(() => {
    if (!parsedAddress) return;
    let cancelled = false;
    setAddressExists(false);
    setError(null);
    const timeoutId = setTimeout(() => {
      checkPropertyExists({
        address: parsedAddress.address,
        unit: normalizeUnit(unit) || undefined,
        city: parsedAddress.city,
        state: parsedAddress.state,
        zipCode: parsedAddress.zipCode,
      })
        .then((res) => {
          if (cancelled || !res.exists) return;
          setAddressExists(true);
          setError('This property already exists in the system.');
        })
        .catch(() => {
          /* ignore — non-critical duplicate check */
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [parsedAddress, unit]);

  const canSubmit =
    parsedAddress && !addressExists && !isSubmitting && (submitOnly || selectedDeal);

  const handleSubmit = async () => {
    if (!parsedAddress || (!submitOnly && !selectedDeal)) return;

    setError(null);
    setIsSubmitting(true);

    if (submitOnly) {
      const fullAddress = `${formatStreetAddress(parsedAddress.address, unit)}, ${
        parsedAddress.city
      }, ${parsedAddress.state} ${parsedAddress.zipCode}`;
      onPropertyAdded?.(fullAddress);
      handleClose();
      return;
    }

    try {
      const { property } = await createProperty({
        address: parsedAddress.address,
        unit: normalizeUnit(unit) || undefined,
        city: parsedAddress.city,
        state: parsedAddress.state,
        zipCode: parsedAddress.zipCode,
        latitude: parsedAddress.latitude,
        longitude: parsedAddress.longitude,
      });

      if (selectedFile) {
        beginProcessingIndicator();
        const [presigned] = await generatePreSignedUrls({
          files: [
            {
              filename: selectedFile.name,
              mimeType: selectedFile.type || 'application/octet-stream',
              isExtractable: true,
            },
          ],
        });
        await fetch(presigned.preSignedUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: { 'Content-Type': selectedFile.type || 'application/octet-stream' },
        });
        await createDocuments({
          propertyId: property.id,
          source: reportSource,
          documents: [
            {
              filename: selectedFile.name,
              fileSize: selectedFile.size,
              mimeType: selectedFile.type || 'application/octet-stream',
              s3Key: presigned.key,
            },
          ],
        });
      }

      // Assign property to selected deal: create listing (group membership grants access)
      if (selectedDeal) {
        const fullAddr = `${formatStreetAddress(parsedAddress.address, unit)}, ${
          parsedAddress.city
        }, ${parsedAddress.state} ${parsedAddress.zipCode}`;
        await createListing.mutateAsync({
          dealId: selectedDeal.deal.id,
          data: { address: fullAddr, status: 'Touring', propertyId: property.id },
        });
      }

      showAlert(
        selectedFile ? t('property.added_with_report') : t('property.added_to_deal'),
        'success',
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['properties'] }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'properties'] }),
        queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY }),
      ]);
      onPropertyAdded?.(formatStreetAddress(parsedAddress.address, unit));
      handleClose();
    } catch {
      setError('Failed to add property. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setAddressQuery('');
    setSelectedAddress(null);
    setParsedAddress(null);
    setUnit('');
    setSelectedFile(null);
    setReportSource('seller');
    setError(null);
    setAddressExists(false);
    setIsSubmitting(false);
    setSelectedDeal(null);
    setDealDropdownOpen(false);
    setDealSearch('');
    onClose();
  };

  const ALLOWED_REPORT_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
  ];

  const pickReport = (f: File | undefined) => {
    if (!f) return;
    if (!ALLOWED_REPORT_TYPES.includes(f.type)) {
      setError('Please upload a PDF, DOCX, JPG, or PNG file');
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      setError('File size must be less than 25MB');
      return;
    }
    setSelectedFile(f);
    setError(null);
  };

  const selectedGroupName = selectedDeal
    ? groups?.find((g) => g.id === selectedDeal.groupId)?.holdingName
    : undefined;

  const foot = (
        // the house dialog foot: a quiet cancel, and the one lime action
        <>
          <Button variant="ghost" disabled={isSubmitting} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="add-property-submit-btn">
            {isSubmitting ? 'Adding…' : 'Add property'}
          </Button>
        </>
  );
  const body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
        {/* Address: the search, then the resolved address beside its unit */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <AddressSearch
            id="add-property-address"
            options={addressOptions}
            value={selectedAddress}
            onChange={setSelectedAddress}
            inputValue={addressQuery}
            onInputChange={(newInputValue) => {
              setAddressQuery(newInputValue);
              if (selectedAddress) setSelectedAddress(null);
            }}
            loading={isSearching}
            testId="add-property-address-input"
          />

          {parsedAddress && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 136px' },
                alignItems: 'end',
                gap: '12px',
              }}>
              {!addressExists ? (
                <AddressPreview
                  testId="add-property-address-preview"
                  street={formatStreetAddress(parsedAddress.address, unit)}
                  locality={`${parsedAddress.city}, ${parsedAddress.state} ${parsedAddress.zipCode}`}
                />
              ) : (
                <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
              )}
              <Field label="Unit / apt" htmlFor="add-property-unit">
                <TextInput
                  id="add-property-unit"
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                  placeholder="Optional"
                  inputProps={{ 'aria-label': 'Unit or apartment number', maxLength: 30 }}
                  fullWidth
                />
              </Field>
            </Box>
          )}
        </Box>

        {/* Inspection report: the sleeve tile, still optional, still a drop target */}
        <ReportSleeve
          inputId="add-property-file-input"
          label="Inspection report"
          file={selectedFile}
          onInputChange={(e) => pickReport(e.target.files?.[0])}
          onDropFile={pickReport}
          onRemove={() => setSelectedFile(null)}
          reportSource={reportSource}
          onReportSourceChange={setReportSource}
          isMobile={isMobile}
          testId="add-property-file-upload"
        />

        {/* Client / deal selector */}
        {!submitOnly && (
          <Field label="Assign to deal">
            <Box ref={dealDropdownRef} sx={{ minWidth: 0 }}>
              <ButtonBase
                onClick={() => setDealDropdownOpen((o) => !o)}
                aria-expanded={dealDropdownOpen}
                aria-haspopup="listbox"
                sx={{
                  width: '100%',
                  height: 40,
                  px: '12px',
                  gap: '8px',
                  justifyContent: 'flex-start',
                  borderRadius: '12px',
                  backgroundColor: 'var(--tx-fill)',
                  fontFamily: 'inherit',
                  transition: 'background-color .15s ease',
                  '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
                  '&.Mui-focusVisible': { outline: '2px solid var(--tx-focus)', outlineOffset: 2 },
                }}>
                <Box component="span" sx={{ display: 'flex', color: 'var(--tx-faint)', flex: 'none' }}>
                  <Icon name="team" size={16} stroke={1.6} />
                </Box>
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    fontSize: 'var(--tx-field-size)',
                    fontWeight: selectedDeal ? 600 : 500,
                    color: selectedDeal ? 'var(--tx-text)' : 'var(--tx-faint)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {selectedDeal ? `${selectedGroupName} → ${selectedDeal.deal.name}` : 'Select a client and deal'}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    color: 'var(--tx-faint)',
                    flex: 'none',
                    transform: dealDropdownOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}>
                  <Icon name="chev" size={16} stroke={1.6} />
                </Box>
              </ButtonBase>
              {dealDropdownOpen &&
                (() => {
                  const q = dealSearch.trim().toLowerCase();
                  const filtered = (groups ?? [])
                    .map((g) => {
                      const activeDeals = g.deals.filter((d) => d.status !== 'Closed');
                      if (!q) return { group: g, deals: activeDeals };
                      const matchGroup = g.holdingName.toLowerCase().includes(q);
                      const matchDeals = activeDeals.filter((d) => d.name.toLowerCase().includes(q));
                      if (matchGroup) return { group: g, deals: activeDeals };
                      if (matchDeals.length > 0) return { group: g, deals: matchDeals };
                      return null;
                    })
                    .filter(
                      (x): x is { group: (typeof groups)[0]; deals: Deal[] } =>
                        x !== null && x.deals.length > 0,
                    );

                  return (
                    // the list opens in place as a tonal well under the select, its search on top
                    <Box
                      role="listbox"
                      sx={{
                        mt: '6px',
                        p: '6px',
                        borderRadius: '12px',
                        backgroundColor: 'var(--tx-fill)',
                        maxHeight: 280,
                        overflowY: 'auto',
                      }}>
                      <TextInput
                        autoFocus
                        size="small"
                        icon="search"
                        fullWidth
                        value={dealSearch}
                        onChange={(e) => setDealSearch(e.target.value)}
                        placeholder="Search clients or deals…"
                        sx={{ backgroundColor: 'var(--tx-fill)', borderRadius: '9px' }}
                      />
                      {filtered.length === 0 && (
                        <Box sx={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-faint)', p: '10px 8px 6px' }}>
                          {q ? 'No matches found' : 'No client groups found'}
                        </Box>
                      )}
                      {filtered.map(({ group: g, deals }) => (
                        <Box key={g.id} sx={{ pb: '2px' }}>
                          <Box sx={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-faint)', p: '12px 8px 4px' }}>
                            {g.holdingName}
                          </Box>
                          {deals.map((d) => {
                            const isSelected = selectedDeal?.deal.id === d.id;
                            return (
                              <Box
                                key={d.id}
                                role="option"
                                aria-selected={isSelected}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDeal({ deal: d, groupId: g.id });
                                  setDealDropdownOpen(false);
                                  setDealSearch('');
                                }}
                                sx={[
                                  {
                                    p: '8px 10px',
                                    cursor: 'pointer',
                                    borderRadius: '9px',
                                    '&:hover': { backgroundColor: 'var(--tx-fill)' },
                                  },
                                  isSelected && selectedSx,
                                ]}>
                                <Box
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: isSelected ? 600 : 500,
                                    letterSpacing: '-0.01em',
                                    color: 'var(--tx-text)',
                                  }}>
                                  {d.name}
                                </Box>
                                <Meta>
                                  {d.periodType} · Started {d.engagementStart}
                                </Meta>
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
            </Box>
          </Field>
        )}

        {/* Error */}
        {error && (
          <Box
            role="alert"
            data-testid="add-property-error"
            sx={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-danger)', mt: '-8px' }}>
            {error}
          </Box>
        )}
      </Box>
  );

  if (anchorEl && !isMobile) {
    // the drop-down form: a solid panel hanging from its button (Tradehaus .tx-pop), not a screen takeover
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => !isSubmitting && handleClose()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            'data-testid': 'add-property-modal',
            sx: {
              mt: '8px',
              width: 460,
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100dvh - 120px)',
              overflowY: 'auto',
              boxSizing: 'border-box',
              p: '20px',
              backgroundColor: 'var(--tx-dialog)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'var(--tx-blur-panel)',
              WebkitBackdropFilter: 'var(--tx-blur-panel)',
              borderRadius: '20px',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
            },
          } as object,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', mt: '-4px', mr: '-4px' }}>
          <Box component="span" sx={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)' }}>
            Add a property
          </Box>
          <CloseButton size="sm" onClose={() => !isSubmitting && handleClose()} />
        </Box>
        {body}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '20px' }}>{foot}</Box>
      </Popover>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      width={480}
      disableClose={isSubmitting}
      title="Add a property"
      titleProps={{ sx: ADD_PROPERTY_TITLE_SX }}
      subtitle="Add a property to your portfolio. Assign it to clients and manage documents and reports."
      containerProps={{ gap: '24px', p: { xs: '24px 20px', sm: '28px' } }}
      actions={foot}
      data-testid="add-property-modal">
      {body}
    </Dialog>
  );
};
